import { Box, Typography, CircularProgress } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import PropTypes from "prop-types";
import { useState, useEffect } from "react";
import SubstitutionsTable from "./SubstitutionsTable.jsx";
import ViewAllLink from "./ViewAllLink.jsx";
import { useSnackbar } from "notistack";
import { backendGet } from "@accredo/fe-shared/services/dataService.js";
import EmptyRowMessage from "./tables/EmptyRowMessage.jsx";
import {
  colorsFoundation,
  componentHeights,
  dimensions,
  typographyTokens,
} from "@accredo/fe-shared/theme/index.js";

/**
 * HomeSubstitutionsCard Component
 * Displays a card with two flush/bare tables showing the current user's substitutions:
 * - "Ich vertrete" (users I am substituting for)
 * - "Ich werde vertreten von" (users who are substituting for me)
 *
 * Reuses the existing SubstitutionsTable component with appropriate variants.
 */
export default function HomeSubstitutionsCard({
  title = "Meine Vertretungen",
}) {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const borderColor = theme.palette.grey[200];

  // State for fetched data
  const [substitutingFor, setSubstitutingFor] = useState([]);
  const [substitutedBy, setSubstitutedBy] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);

  // Fetch data once
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const response = await backendGet("/data/substitutions/own", {
          label: "Stellvertretungen laden",
          enqueueSnackbar,
        });

        setSubstitutingFor(response?.substituting_for || []);
        setSubstitutedBy(response?.substituted_by || []);
        setHasLoaded(true);
      } catch (error) {
        console.error("Failed to fetch substitutions:", error);
        setSubstitutingFor([]);
        setSubstitutedBy([]);
        setHasLoaded(true);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [enqueueSnackbar]);

  return (
    <Box
      sx={{
        borderRadius: theme.spacing(1),
        border: `1px solid ${borderColor}`,
        overflow: "hidden",
        backgroundColor: theme.palette.background.paper,
      }}
    >
      {/* Title section */}
      {title && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            backgroundColor: theme.palette.grey[100],
            px: theme.spacing(4),
            py: theme.spacing(2.5),
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontSize: typographyTokens.size.base,
              fontWeight: typographyTokens.weight.semibold,
              color: colorsFoundation.neutral[800],
              mb: 0,
              lineHeight: 1.5,
            }}
          >
            {title}
          </Typography>
          <ViewAllLink label="Verwalten" to="/substitutions" />
        </Box>
      )}

      {isLoading && !hasLoaded ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: theme.spacing(24),
          }}
        >
          <CircularProgress color="primary" size={32} thickness={5} />
        </Box>
      ) : substitutingFor.length === 0 && substitutedBy.length === 0 ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            color: theme.palette.text.secondary,
            minHeight: componentHeights.homeTopCards,
          }}
        >
          <EmptyRowMessage>Keine Vertretungen vorhanden</EmptyRowMessage>
        </Box>
      ) : (
        <Box>
          {/* Substituting For Section - only render if there are entries */}
          {substitutingFor.length > 0 && (
            <Box>
              <Box
                sx={{
                  px: theme.spacing(4),
                  pt: dimensions["2xl"],
                }}
              >
                <Typography
                  sx={{
                    fontWeight: typographyTokens.weight.semibold,
                    fontSize: typographyTokens.size.sm,
                    color: theme.palette.text.primary,
                  }}
                >
                  Ich vertrete
                </Typography>
              </Box>
              <SubstitutionsTable
                variant="bare"
                scope="substituting_for"
                data={substitutingFor}
                limit={10}
              />
            </Box>
          )}

          {/* Substituted By Section - only render if there are entries */}
          {substitutedBy.length > 0 && (
            <Box
              sx={{
                borderTop:
                  substitutingFor.length > 0
                    ? `1px solid ${borderColor}`
                    : "none",
              }}
            >
              <Box
                sx={{
                  px: theme.spacing(4),
                  py: theme.spacing(1.5),
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.text.primary,
                  }}
                >
                  Ich werde vertreten von
                </Typography>
              </Box>
              <SubstitutionsTable
                variant="bare"
                scope="substituted_by"
                data={substitutedBy}
                limit={10}
              />
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}

HomeSubstitutionsCard.propTypes = {
  title: PropTypes.string,
};
